import { useEffect, useRef } from 'react';
import { attachClientImpressionHandler } from './client-event-tracking';
import { getElementData } from '@buzzfeed/bf-utils/lib/affiliate-link';

export function useAffiliateLinkImpressionTracking({ buzz = {}, subbuzz_id = null }) {
  const ref = useRef(null);
  const detachHandlersRef = useRef([]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Clear any existing handlers
    detachHandlersRef.current.forEach(detach => detach());
    detachHandlersRef.current = [];

    // Set up tracking for a link
    const setupTrackingForLink = (link) => {
      const isPopularProductsLink = link.dataset.component === 'popular-products';
      const data = getElementData(link);
      const detachImpression = attachClientImpressionHandler(
        link,
        buzz,
        {
          link_id: data['link-id'],
          item_name: subbuzz_id,
          item_type: 'text',
          target_content_type: 'url',
          target_content_id: link.href,
          unit_name: isPopularProductsLink ? 'popular_products' : buzz.id,
          unit_type: 'buzz_body',
          subunit_type: isPopularProductsLink ? 'package' : '',
          subunit_name: isPopularProductsLink ? 'popular_products' : '',
        }
      );
      detachHandlersRef.current.push(detachImpression);
    };

    // Wait for all links to have ButtonPostTap classes applied
    const waitForButtonPostTapClasses = () => {
      return new Promise((resolve) => {
        const affiliateLinks = element.querySelectorAll('a[data-affiliate]');
        
        if (affiliateLinks.length === 0) {
          resolve();
          return;
        }

        let completedLinks = 0;
        const totalLinks = affiliateLinks.length;

        affiliateLinks.forEach((link) => {
          const isSkipped = link.classList.contains('js-buttonposttap-skipped');
          const isModified = link.classList.contains('js-buttonposttap-modified');

          if (isSkipped || isModified) {
            completedLinks++;
            if (completedLinks === totalLinks) {
              resolve();
            }
          } else {
            // Watch for either class to be added
            const observer = new MutationObserver((mutationsList) => {
              mutationsList.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                  if (
                    link.classList.contains('js-buttonposttap-skipped') ||
                    link.classList.contains('js-buttonposttap-modified')
                  ) {
                    completedLinks++;
                    observer.disconnect();
                    if (completedLinks === totalLinks) {
                      resolve();
                    }
                  }
                }
              });
            });
            observer.observe(link, { attributes: true, attributeFilter: ['class'] });
          }
        });

        // Fallback timeout to prevent hanging
        setTimeout(resolve, 350);
      });
    };

    // Wait for ButtonPostTap classes, then set up tracking
    waitForButtonPostTapClasses().then(() => {
      element.querySelectorAll('a[data-affiliate]').forEach((link) => {
        setupTrackingForLink(link);
      });
    });

    return () => {
      detachHandlersRef.current.forEach(detach => detach());
      detachHandlersRef.current = [];
    };
  }, [buzz, subbuzz_id]);

  return ref;
}
